import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, MapPin, Phone, Mail, CheckCircle2, Circle, Camera,
  Image as ImageIcon, Wrench, Zap, X, Upload,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

function JobDetail({ projectId, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get(`/installer/jobs/${projectId}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auftrag konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const markStarted = async () => {
    setBusy("started");
    try {
      const r = await api.post(`/installer/jobs/${projectId}/installation-started`, { note: "" });
      if (r.data.already_done) toast.info("Bereits markiert");
      else toast.success("Montagebeginn markiert — Sales-Rep wurde benachrichtigt");
      load(); onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(null); }
  };

  const markCommissioned = async () => {
    setBusy("commissioned");
    try {
      const r = await api.post(`/installer/jobs/${projectId}/commissioning-done`, { note: "" });
      if (r.data.already_done) toast.info("Bereits markiert");
      else toast.success("Inbetriebnahme markiert — Sales-Rep wurde benachrichtigt");
      load(); onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(null); }
  };

  const uploadPhoto = async (file, kind) => {
    if (!file) return;
    setBusy("photo");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      await api.post(`/installer/jobs/${projectId}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto hochgeladen");
      load(); onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally { setBusy(null); }
  };

  const installStarted = data?.workflow?.steps?.find((s) => s.key === "installation_started")?.completed;
  const commissioningDone = data?.workflow?.steps?.find((s) => s.key === "commissioning_done")?.completed;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-2xl max-h-[95vh] bg-[#0A0A0B] border border-zinc-800 rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col" data-testid="installer-job-detail">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3 bg-[#09090B]">
          <div className="min-w-0">
            <div className="text-[12px] uppercase tracking-wider text-zinc-300 font-semibold">Auftrag</div>
            <div className="text-[14px] text-zinc-100 font-bold truncate">{data?.customer?.name || "Lade…"}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-800"><X size={18} className="text-zinc-400" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {loading && <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#00E575]" size={20} /></div>}

          {!loading && data && (
            <>
              {/* Customer contact */}
              <div className="rounded-xl bg-[#0F0F11] border border-zinc-800 p-3 space-y-1.5">
                <div className="text-[12px] font-mono uppercase tracking-[0.18em] text-zinc-300">Kontakt</div>
                {data.customer.phone && <div className="text-[13px] text-zinc-100 flex items-center gap-1.5"><Phone size={12} /><a href={`tel:${data.customer.phone}`} className="text-[#00E575]">{data.customer.phone}</a></div>}
                {data.customer.email && <div className="text-[12px] text-zinc-400 flex items-center gap-1.5 font-medium"><Mail size={11} />{data.customer.email}</div>}
                {data.customer.address && <div className="text-[12px] text-zinc-400 flex items-center gap-1.5 font-medium"><MapPin size={11} />{data.customer.address}</div>}
              </div>

              {/* Checklist actions */}
              <div className="rounded-xl bg-[#0F0F11] border border-zinc-800 p-3 space-y-2.5">
                <div className="text-[12px] font-mono uppercase tracking-[0.18em] text-zinc-300 mb-1">Montage-Status</div>

                <div className="flex items-center gap-2">
                  {installStarted ? <CheckCircle2 size={18} className="text-[#00E575]" /> : <Circle size={18} className="text-zinc-600" />}
                  <span className={`flex-1 text-[13px] ${installStarted ? "text-zinc-300" : "text-zinc-100 font-semibold"}`}>Montagebeginn</span>
                  {!installStarted && (
                    <button onClick={markStarted} disabled={busy === "started"} data-testid="installer-mark-started" className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] hover:bg-[#00C964] inline-flex items-center gap-1.5 disabled:opacity-50">
                      {busy === "started" ? <Loader2 size={12} className="animate-spin" /> : <><Wrench size={12} /> Start markieren</>}
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {commissioningDone ? <CheckCircle2 size={18} className="text-[#00E575]" /> : <Circle size={18} className="text-zinc-600" />}
                  <span className={`flex-1 text-[13px] ${commissioningDone ? "text-zinc-300" : installStarted ? "text-zinc-100 font-semibold" : "text-zinc-500"}`}>Inbetriebnahme abgeschlossen</span>
                  {!commissioningDone && (
                    <button onClick={markCommissioned} disabled={busy === "commissioned" || !installStarted} data-testid="installer-mark-commissioned" className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] hover:bg-[#00C964] inline-flex items-center gap-1.5 disabled:opacity-40">
                      {busy === "commissioned" ? <Loader2 size={12} className="animate-spin" /> : <><Zap size={12} /> Inbetriebnahme</>}
                    </button>
                  )}
                </div>
              </div>

              {/* Photo upload */}
              <div className="rounded-xl bg-[#0F0F11] border border-zinc-800 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[12px] font-mono uppercase tracking-[0.18em] text-zinc-300">Fotos ({(data.photos || []).length})</div>
                  <div className="flex gap-1.5">
                    <PhotoUpload onPick={(f) => uploadPhoto(f, "before")} label="Vorher" testid="installer-photo-before" />
                    <PhotoUpload onPick={(f) => uploadPhoto(f, "after")} label="Nachher" testid="installer-photo-after" />
                  </div>
                </div>
                {(data.photos || []).length === 0 ? (
                  <div className="text-[11.5px] text-zinc-600 text-center py-3 italic">Noch keine Fotos</div>
                ) : (
                  <div className="grid grid-cols-3 gap-1.5">
                    {data.photos.map((ph) => (
                      <a key={ph.id} href={fileUrl(ph.storage_path)} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded-lg overflow-hidden bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/40 relative">
                        <img src={fileUrl(ph.storage_path)} alt={ph.caption || ph.filename} className="w-full h-full object-cover" />
                        <span className={`absolute top-1 left-1 text-[9px] font-bold uppercase tracking-wider px-1 rounded ${ph.kind === "before" ? "bg-amber-500/80 text-amber-50" : "bg-[#00E575]/80 text-[#09090B]"}`}>{ph.kind}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PhotoUpload({ onPick, label, testid }) {
  return (
    <label className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12px] font-semibold cursor-pointer">
      <Camera size={11} /> {label}
      <input
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid={testid}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }}
      />
    </label>
  );
}

export default function InstallerJobs() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/installer/jobs");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aufträge konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <AppHeader />
      <div className="px-4 sm:px-6 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-[20px] sm:text-[24px] font-bold inline-flex items-center gap-2">
              <Wrench size={20} className="text-[#00E575]" /> Meine Montage-Aufträge
            </h1>
            <p className="text-[12px] text-zinc-300 mt-0.5 font-medium">
              Status-Markierungen lösen automatisch eine Push-Nachricht an den Sales-Rep aus.
            </p>
          </div>
          <button
            onClick={load}
            className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[12px] inline-flex items-center gap-1.5 font-medium"
            data-testid="installer-refresh"
          >
            <RefreshCw size={12} /> Aktualisieren
          </button>
        </div>
      </div>

      {loading && <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[#00E575]" size={22} /></div>}

      {!loading && data && data.jobs.length === 0 && (
        <div className="px-4 py-16 text-center text-zinc-500" data-testid="installer-empty">
          Aktuell sind keine Aufträge zugewiesen. Sobald die Vertriebsleitung Sie einem Projekt zuweist, erscheint es hier.
        </div>
      )}

      {!loading && data && data.jobs.length > 0 && (
        <div className="px-3 sm:px-4 pb-8 space-y-2.5" data-testid="installer-jobs-list">
          {data.jobs.map((j) => {
            const startedColor = j.installation_started ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-zinc-800 text-zinc-400 border-zinc-700";
            const commColor = j.commissioning_done ? "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40" : "bg-zinc-800 text-zinc-500 border-zinc-700";
            return (
              <button
                key={j.project_id}
                onClick={() => setSelectedId(j.project_id)}
                data-testid={`installer-job-${j.project_id}`}
                className="w-full text-left rounded-xl bg-[#0F0F11] hover:bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/40 p-3 transition"
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="font-display font-bold text-[14px] text-zinc-100 truncate flex-1">{j.customer_name}</div>
                  <span className="text-[12px] font-mono text-zinc-300 flex-shrink-0">
                    <ImageIcon size={10} className="inline mr-0.5" />{j.photo_count}
                  </span>
                </div>
                {j.address && <div className="text-[11.5px] text-zinc-400 flex items-center gap-1 mb-1.5"><MapPin size={11} />{j.address}</div>}
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <span className={`text-[12px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 border ${startedColor}`}>
                    {j.installation_started ? `Montage gestartet ${fmtDate(j.installation_started_at)}` : "Montage offen"}
                  </span>
                  <span className={`text-[12px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 border ${commColor}`}>
                    {j.commissioning_done ? `IBN ${fmtDate(j.commissioning_done_at)}` : "IBN offen"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selectedId && (
        <JobDetail
          projectId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdated={load}
        />
      )}
    </div>
  );
}
